import type { Query, ResultSet } from '@cubejs-client/core'
import { useQueries, useQueryClient, type QueryKey } from '@tanstack/react-query'
import { useCallback, useEffect, useEffectEvent, useMemo, useState } from 'react'

import {
  BLOCK_SIZE,
  blocksForRange,
  loadedRowCount,
  pickTotal,
  rowAt,
  type LoadedBlocks,
} from '@/components/data-table/virtual-blocks'
import { cubeApi, getCubeLiveApi } from '@/lib/cube'

/** Bir bloğun yanıtı: satırlar + (aynı filtreli) toplam satır sayısı. */
export interface BlockPage<T> {
  items: T[]
  total: number
}

export interface CubeBlocksOptions<T> {
  /** Veri kümesinin kimliği (süzgeç + sıralama); değişince bloklar başa döner. */
  datasetKey: string
  /** `queryKeys.cube(view, …)` kalıbı: realtime `['cube', view]` önekiyle tazeler. */
  queryKey: (block: number) => QueryKey
  query: (block: number) => Query
  select: (result: ResultSet) => BlockPage<T>
  blockSize?: number
}

interface BlocksView<T> {
  blocks: LoadedBlocks<T>
  total: number
  hasFirst: boolean
  firstPending: boolean
  isError: boolean
  error: unknown
}

interface RangeState {
  key: string
  range: { start: number; end: number } | null
  /** İstenmiş tüm bloklar (kaydırılıp uzaklaşılanlar önbellekte gözlenmeye devam eder). */
  ever: readonly number[]
}

const INITIAL_BLOCKS: readonly number[] = [0]

/**
 * Sanal kaydırma için blok blok Cube yükleyici (docs/DATA-GRID-PLAN.md Faz 5). Her blok kendi sorgusudur
 * (`limit`/`offset`); yalnız görünen aralığı kaplayan bloklar istenir ve canlı abonelik alır. Uzaklaşılan
 * bloklar önbellekte kalır (bayat), geri gelince yenilenir. Sıralama/süzgeç değişince (`datasetKey`) yeni bloklar
 * istenir; yenisi gelene kadar eski veri ekranda kalır (`isPlaceholder`). Realtime `['cube', view, …]`
 * invalidation'ı yüklü blokları yerinde yeniler, kaydırma yeri bozulmaz.
 */
export function useCubeBlocks<T>({
  datasetKey,
  queryKey,
  query,
  select,
  blockSize = BLOCK_SIZE,
}: CubeBlocksOptions<T>) {
  const queryClient = useQueryClient()
  const [state, setState] = useState<RangeState>({
    key: datasetKey,
    range: null,
    ever: INITIAL_BLOCKS,
  })
  const current =
    state.key === datasetKey ? state : { key: datasetKey, range: null, ever: INITIAL_BLOCKS }

  const active = useMemo(
    () => (current.range ? blocksForRange(current.range.start, current.range.end, blockSize) : [0]),
    [current.range, blockSize],
  )
  const activeKey = active.join()
  const everKey = current.ever.join()

  // Yalnız abonelik efektinde okunur (render'da çağrılamaz): son render'ın kurucularını görür.
  const selectLatest = useEffectEvent(select)
  const queryLatest = useEffectEvent(query)
  const queryKeyLatest = useEffectEvent(queryKey)

  const combine = useCallback(
    (
      results: {
        data?: BlockPage<T>
        status: string
        error: unknown
        isFetching: boolean
        dataUpdatedAt: number
      }[],
    ): BlocksView<T> => {
      const blocks: Record<number, readonly T[]> = {}
      const entries: { total: number; updatedAt: number }[] = []
      let error: unknown
      let isError = false
      results.forEach((result, position) => {
        const block = current.ever[position] as number
        if (result.data) {
          blocks[block] = result.data.items
          entries.push({ total: result.data.total, updatedAt: result.dataUpdatedAt })
        } else if (result.status === 'error') {
          isError = true
          error = result.error
        }
      })
      const first = results[current.ever.indexOf(0)]
      return {
        blocks,
        total: pickTotal(entries),
        hasFirst: Boolean(first?.data),
        firstPending: first !== undefined && !first.data && first.status === 'pending',
        isError,
        error,
      }
    },
    // `ever` her render yeni dizi olmasın diye anahtarla izlenir.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [everKey],
  )

  const view = useQueries({
    queries: current.ever.map((block) => ({
      queryKey: queryKey(block),
      queryFn: async () => select(await cubeApi.load(query(block))),
      enabled: active.includes(block),
    })),
    combine,
  })

  // Yeni veri kümesinin ilk bloğu gelene kadar önceki küme ekranda kalır.
  const [snapshot, setSnapshot] = useState<BlocksView<T> | null>(null)
  if (view.hasFirst && snapshot?.blocks !== view.blocks) setSnapshot(view)
  const shown = view.hasFirst || snapshot === null ? view : snapshot
  const isPlaceholder = !view.hasFirst && snapshot !== null

  const getRow = useCallback(
    (index: number) => rowAt(shown.blocks, index, blockSize),
    [shown.blocks, blockSize],
  )

  const requestRange = useCallback(
    (range: { start: number; end: number }) => {
      setState((prev) => {
        const base =
          prev.key === datasetKey ? prev : { key: datasetKey, range: null, ever: INITIAL_BLOCKS }
        const needed = blocksForRange(range.start, range.end, blockSize)
        const ever = [...new Set([...base.ever, ...needed])].sort((a, b) => a - b)
        const sameRange = base.range?.start === range.start && base.range.end === range.end
        const sameEver = ever.length === base.ever.length
        if (prev === base && sameRange && sameEver) return prev
        return { key: datasetKey, range, ever: sameEver ? base.ever : ever }
      })
    },
    [datasetKey, blockSize],
  )

  // Canlı abonelik yalnız görünen bloklarda; gelen sonuç aynı anahtarla önbelleğe yazılır.
  useEffect(() => {
    const subscriptions = active.map((block) =>
      getCubeLiveApi().subscribe(queryLatest(block), {}, (error, resultSet) => {
        if (error || !resultSet) {
          console.warn('Cube canlı abonelik hatası', error)
          return
        }
        queryClient.setQueryData(queryKeyLatest(block), selectLatest(resultSet))
      }),
    )
    return () => {
      for (const subscription of subscriptions) void subscription.unsubscribe()
    }
    // Abonelik veri kümesi ve görünen bloklar değişince yenilenir; sorgu kurucular son hâlleriyle okunur.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [datasetKey, activeKey, queryClient])

  const refetch = useCallback(() => {
    for (const block of current.ever) {
      void queryClient.refetchQueries({ queryKey: queryKey(block), exact: true })
    }
  }, [current.ever, queryClient, queryKey])

  return {
    getRow,
    total: shown.total,
    loadedCount: useMemo(() => loadedRowCount(shown.blocks), [shown.blocks]),
    isLoading: !view.hasFirst && snapshot === null && view.firstPending,
    isPlaceholder,
    isError: view.isError && !shown.blocks[0],
    error: view.error,
    refetch,
    requestRange,
  }
}
